import { useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import dayjs from "dayjs"
import { fetchTicketedBookableDates, fetchTicketedStartTimes } from "store/experiences"
import { getTicketedSlotsByDate, getTicketedStartLabel } from "utils/experienceAvailability"

const MONTH_KEY = "YYYY-MM"
const DATE_FORMAT = "YYYY-MM-DD"

// One start time from the API in the panel's shape: { id, startsAt, label, isSoldOut, remaining,
// remainingByType: { [ticketTypeId]: places left } }.
const toSlot = (experience, slot) => {
  const remaining = Math.max(0, Number(slot.remaining) || 0)
  return {
    id: slot.id,
    startsAt: slot.starts_at,
    label: getTicketedStartLabel(experience, slot.starts_at),
    isSoldOut: !!slot.sold_out || remaining === 0,
    remaining,
    remainingByType: Object.fromEntries(
      (slot.ticket_types || []).map((type) => [type.ticket_type_id, Math.max(0, Number(type.remaining) || 0)])
    )
  }
}

// Live availability for the ticketed booking panel (backend C2): the bookable days of the month on
// show and the chosen date's start times with places left. Until a month's days or a date's start
// times arrive, or when the request fails, the trip's own start times (time_slots) stand in, without
// places left, as before. Start times past the cut-off are left out; sold-out ones stay, to show as
// such. A new `refreshKey` asks for the date's start times again (e.g. after Add to Cart was refused).
// Shared by the guest widget's panel and the guide's New Booking (`isEmbedded` false: asked as the
// guide portal).
const useTicketedAvailability = ({ experienceId, experience, date, month, refreshKey = 0, isEmbedded = true }) => {
  const dispatch = useDispatch()
  const fallbackByDate = useMemo(() => getTicketedSlotsByDate(experience), [experience])
  // { "YYYY-MM": ["YYYY-MM-DD"] } for each month the API answered.
  const [datesByMonth, setDatesByMonth] = useState({})
  const [daySlotsState, setDaySlotsState] = useState({ date: null, slots: null, isLoading: false })
  const monthKey = month ? dayjs(month).format(MONTH_KEY) : null
  const isMonthLoaded = !!monthKey && !!datesByMonth[monthKey]

  useEffect(() => {
    if (!experienceId || !monthKey || isMonthLoaded) return undefined
    let isCurrent = true
    const start = dayjs(`${monthKey}-01`)
    dispatch(fetchTicketedBookableDates(experienceId, start.format(DATE_FORMAT), start.endOf("month").format(DATE_FORMAT), isEmbedded))
      .then((response) => {
        const dates = response?.data?.bookable_dates
        if (isCurrent && Array.isArray(dates)) setDatesByMonth((prev) => ({ ...prev, [monthKey]: dates }))
      })
      .catch(() => {})
    return () => {
      isCurrent = false
    }
  }, [dispatch, experienceId, monthKey, isMonthLoaded, isEmbedded])

  useEffect(() => {
    if (!experienceId || !date) return undefined
    let isCurrent = true
    setDaySlotsState({ date, slots: null, isLoading: true })
    dispatch(fetchTicketedStartTimes(experienceId, date, isEmbedded))
      .then((response) => {
        if (!isCurrent) return
        const slots = Array.isArray(response?.data)
          ? response.data
              .filter((slot) => slot?.starts_at && (slot.bookable !== false || slot.sold_out))
              .map((slot) => toSlot(experience, slot))
              .sort((first, second) => dayjs(first.startsAt).valueOf() - dayjs(second.startsAt).valueOf())
          : null
        setDaySlotsState({ date, slots, isLoading: false })
      })
      .catch(() => isCurrent && setDaySlotsState({ date, slots: null, isLoading: false }))
    return () => {
      isCurrent = false
    }
  }, [dispatch, experienceId, experience, date, refreshKey, isEmbedded])

  // A month the API answered uses its days; any other keeps the trip's own start dates.
  const bookableDates = useMemo(() => {
    const fallback = Object.keys(fallbackByDate).filter((day) => !datesByMonth[day.slice(0, 7)])
    return [...fallback, ...Object.values(datesByMonth).flat()].sort((first, second) => first.localeCompare(second))
  }, [fallbackByDate, datesByMonth])

  const isForDate = daySlotsState.date === date
  const isSlotsLoading = isForDate && daySlotsState.isLoading
  const daySlots = useMemo(() => {
    if (!date || isSlotsLoading) return []
    return (isForDate && daySlotsState.slots) || fallbackByDate[date] || []
  }, [date, isSlotsLoading, isForDate, daySlotsState.slots, fallbackByDate])

  return { bookableDates, daySlots, isSlotsLoading }
}

export default useTicketedAvailability
